/**
 * /moniteur/qr/:token — Page publique atterrissage après scan QR du moniteur (Phase 31)
 *
 * L'élève arrive ici après avoir scanné le QR d'un moniteur.
 * Si connecté (rôle eleve ou autre) → bouton "Je rejoins ce moniteur" auto-rattache.
 * Sinon → redirection /register avec next=/moniteur/qr/:token.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  GraduationCap, CheckCircle2, AlertCircle, Loader2, Building2, Award, BookOpen,
  ArrowRight, LogIn, User,
} from 'lucide-react';
import api from '../api';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';
import { SiteFooter } from '../components/SiteFooter';

const TeacherPhoto = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="w-28 h-28 rounded-2xl object-cover border-2 border-white/10 shadow-lg"
    onError={(e) => { e.target.style.display = 'none'; }}
    data-testid="teacher-photo"
  />
);

const FORMULE_LABELS = {
  permis_b: 'Permis B',
  aac: 'AAC',
  permis_b78: 'B78 (BEA)',
  permis_a1: 'A1 (125)',
  permis_a2: 'A2 (moto)',
  passerelle_a: 'Passerelle A',
  formation_125: 'Formation 125',
  code_seul: 'Code seul',
};

export default function MoniteurQrPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useApp() || {};

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [phone, setPhone] = useState('');
  const [formula, setFormula] = useState('permis_b');
  const [autoEcole, setAutoEcole] = useState('');

  useEffect(() => {
    document.title = 'Rejoindre un moniteur · code2roues';
    (async () => {
      try {
        const p = await api.publicMoniteurByQr(token);
        setProfile(p);
      } catch (e) {
        setError(e.message || 'QR invalide');
      }
      setLoading(false);
    })();
  }, [token]);

  const doJoin = async () => {
    if (!user) {
      // Redirection vers la connexion avec next
      const next = encodeURIComponent(`/moniteur/qr/${token}`);
      navigate(`/login?next=${next}`);
      return;
    }
    setJoining(true);
    try {
      const r = await api.moniteurProfileJoin(token, { phone, formula, auto_ecole_name: autoEcole });
      setJoined(true);
      toast.success(`Tu es rattaché à ${r.teacher_name}`, {
        description: r.action === 'updated' ? 'Ton rattachement a été mis à jour' : 'Une fiche a été créée pour toi',
      });
    } catch (e) {
      toast.error(e.message);
    }
    setJoining(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col">
      <header className="border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80">
            <div className="w-8 h-8 rounded-lg bg-rose-600 flex items-center justify-center">
              <GraduationCap size={14} />
            </div>
            <div className="text-[13px] font-semibold">code2roues.fr</div>
          </Link>
          {!user && (
            <Link to="/login" className="text-white/60 text-[12px] inline-flex items-center gap-1.5 hover:text-white transition" data-testid="link-login-topbar">
              <LogIn size={12} /> Connexion
            </Link>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-10">
        {loading && (
          <div className="text-center py-16 text-white/50">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            <div className="text-sm">Chargement de la fiche…</div>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/[0.06] p-6 text-center" data-testid="error-state">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>QR indisponible</h1>
            <p className="text-white/70 text-sm mb-4">{error}</p>
            <Link to="/" className="inline-block text-rose-400 text-sm font-semibold hover:underline">Retour à l&apos;accueil</Link>
          </div>
        )}

        {!loading && profile && !joined && (
          <>
            <div className="text-center mb-8">
              <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Rejoindre un moniteur</div>
              <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="teacher-name">
                {profile.display_name}
              </h1>
              {profile.auto_ecole_name && (
                <div className="text-white/60 text-sm mt-2 inline-flex items-center gap-1.5">
                  <Building2 size={13} /> {profile.auto_ecole_name}
                </div>
              )}
            </div>

            {/* Photo optionnelle */}
            {profile.photo_url && (
              <div className="flex justify-center mb-6">
                <TeacherPhoto src={profile.photo_url} alt={`Photo de ${profile.display_name}`} />
              </div>
            )}

            {/* Bio */}
            {profile.bio && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4" data-testid="teacher-bio">
                <div className="text-[10px] uppercase tracking-widest text-rose-400 font-semibold mb-2">À propos</div>
                <p className="text-white/80 text-[14px] leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
              </div>
            )}

            {/* Grille infos */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {typeof profile.years_experience === 'number' && (
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3" data-testid="teacher-experience">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0">
                    <Award size={16} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Expérience</div>
                    <div className="text-white font-semibold">
                      {profile.years_experience} {profile.years_experience > 1 ? 'ans' : 'an'}
                    </div>
                  </div>
                </div>
              )}
              {profile.specialties && profile.specialties.length > 0 && (
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3" data-testid="teacher-specialties">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0">
                    <BookOpen size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Spécialités</div>
                    <div className="text-white/80 text-[12px] mt-0.5">
                      {profile.specialties.map((s) => FORMULE_LABELS[s] || s).join(' · ')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CTA rattachement */}
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] p-5" data-testid="cta-join">
              <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-2">Rejoindre {profile.display_name}</div>
              <h2 className="text-xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {user ? 'Un clic pour te rattacher' : 'Connecte-toi pour te rattacher'}
              </h2>

              {user && (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-1">Ta formule</label>
                      <select
                        value={formula}
                        onChange={(e) => setFormula(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-white/10 bg-black/40 text-white text-[13px] focus:outline-none focus:border-rose-500/60"
                        data-testid="select-join-formula"
                      >
                        {Object.entries(FORMULE_LABELS).map(([k, v]) => (
                          <option key={k} value={k} className="bg-[#111112]">{v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-1">Téléphone (optionnel)</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="06…"
                        className="w-full h-10 px-3 rounded-lg border border-white/10 bg-black/40 text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60"
                        data-testid="input-join-phone"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-1">
                      Ton auto-école <span className="text-white/30 normal-case tracking-normal text-[10px]">(optionnel — si tu en as une différente ou aucune, laisse vide)</span>
                    </label>
                    <input
                      type="text"
                      value={autoEcole}
                      onChange={(e) => setAutoEcole(e.target.value)}
                      placeholder={profile.auto_ecole_name ? `Par défaut ${profile.auto_ecole_name}` : 'ex. Auto-École du Centre (ou vide)'}
                      maxLength={100}
                      className="w-full h-10 px-3 rounded-lg border border-white/10 bg-black/40 text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60"
                      data-testid="input-join-auto-ecole"
                    />
                  </div>
                  <div className="text-[12px] text-white/60 mb-3 inline-flex items-start gap-1.5">
                    <User size={12} className="shrink-0 mt-0.5" />
                    <span>Connecté en tant que <strong className="text-white">{user.email}</strong></span>
                  </div>
                </>
              )}

              <button
                onClick={doJoin}
                disabled={joining}
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-black shadow-lg shadow-rose-900/40 transition"
                data-testid="btn-join"
              >
                {joining ? (
                  <><Loader2 size={16} className="animate-spin" /> Rattachement en cours…</>
                ) : user ? (
                  <>Je rejoins {profile.display_name} <ArrowRight size={16} /></>
                ) : (
                  <><LogIn size={16} /> Me connecter et rejoindre</>
                )}
              </button>

              {!user && (
                <div className="text-center text-[12px] text-white/50 mt-3">
                  Pas encore de compte ?{' '}
                  <Link to={`/register?next=${encodeURIComponent(`/moniteur/qr/${token}`)}`} className="text-rose-400 hover:underline font-semibold">
                    Créer un compte élève
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {joined && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.08] p-8 text-center" data-testid="joined-state">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-3xl font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              C&apos;est fait !
            </h1>
            <p className="text-white/70 text-[15px] mb-5">
              Tu es maintenant rattaché à <strong className="text-white">{profile.display_name}</strong>.
              Ton moniteur voit ta fiche dans son back-office.
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Link
                to="/eleve/dashboard"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold shadow-md transition"
                data-testid="btn-goto-dashboard"
              >
                Aller à mon espace <ArrowRight size={14} />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 text-[13px] font-semibold transition"
              >
                Retour à l&apos;accueil
              </Link>
            </div>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
